import { useState, useCallback, useRef, useEffect } from "react";
import atCtx from "../imAtContext";
import general from "../../../../../general";
import Dialog from "../../../../../lib/Dialog";

/**
 * 管理输入框内容、@mention、发送文本/媒体消息的全部逻辑。
 * @param {object} opts
 * @param {object} opts.store          - MobX store
 * @param {string} opts.sessionId
 * @param {string} opts.tid            - 群组 ID
 * @param {object} opts.listRef        - 消息列表 DOM ref（媒体上传跟随滚动）
 * @param {object} opts.shouldScrollBottom - 滚动 ref
 * @param {function} opts.scrollToBottom
 * @param {function} opts.setReplyMsg
 * @param {boolean} opts.active        - 页面是否激活（用于监听 @picker 返回结果）
 */
export default function useChatComposer({ store, sessionId, tid, listRef, shouldScrollBottom, scrollToBottom, setReplyMsg, active }) {
  const [inputValue, setInputValue]       = useState('');
  const [mentionedMembers, setMentionedMembers] = useState([]);
  const inputRef = useRef(null);

  // ── @ 成员选择器返回时同步结果 ─────────────────────────────────────────────
  const closeAtPicker = useCallback(() => {
    atCtx.atStart = -1;
    atCtx.atEnd   = -1;
    atCtx.inputValue = '';
    atCtx.result = null;
  }, []);

  useEffect(() => {
    if (!active || atCtx.atStart < 0) return;
    if (atCtx.result) {
      const { nick, accountId } = atCtx.result;
      const mention  = `@${nick} `;
      const atStart  = atCtx.atStart;
      const atEnd    = atCtx.atEnd;
      const base     = atCtx.inputValue;
      const newValue = base.slice(0, atStart) + mention + base.slice(atEnd);
      setInputValue(newValue);
      setMentionedMembers((prev) => [
        ...prev.filter((m) => m.accountId !== accountId),
        { nick, accountId },
      ]);
      closeAtPicker();
      const newPos = atStart + mention.length;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) { el.focus(); el.setSelectionRange(newPos, newPos); }
      }));
    } else {
      const base = atCtx.inputValue;
      setInputValue(base.slice(0, atCtx.atStart) + base.slice(atCtx.atEnd));
      closeAtPicker();
    }
  }, [active, closeAtPicker]);

  // ── 插入文本/emoji ────────────────────────────────────────────────────────
  const insertAtCursor = useCallback((text) => {
    const el = inputRef.current;
    if (!el) { setInputValue((v) => v + text); return; }
    el.focus();
    el.setRangeText(text, el.selectionStart, el.selectionEnd, 'end');
    setInputValue(el.value);
  }, []);

  // ── 插入 @mention ────────────────────────────────────────────────────────
  const insertMention = useCallback((nick, accountId) => {
    const el      = inputRef.current;
    const mention = `@${nick} `;
    const currentVal = el ? el.value : '';
    if (currentVal.includes(mention)) return;
    if (el) {
      el.focus();
      el.setRangeText(mention, el.selectionStart, el.selectionEnd, 'end');
      setInputValue(el.value);
    } else {
      setInputValue((v) => v + mention);
    }
    setMentionedMembers((prev) => [
      ...prev.filter((m) => m.accountId !== accountId),
      { nick, accountId },
    ]);
  }, []);

  const clearInput = useCallback(() => {
    setInputValue('');
    setMentionedMembers([]);
    closeAtPicker();
  }, [closeAtPicker]);

  // ── 构建 @mention ext（yxAitMsg）─────────────────────────────────────────
  const buildAtExt = useCallback((text, members) => {
    if (!members.length) return undefined;
    const yxAitMsg = {};
    members.forEach(({ nick, accountId }) => {
      const alias    = `@${nick} `;
      const segments = [];
      let pos = text.indexOf(alias);
      while (pos !== -1) {
        segments.push({ start: pos, end: pos + alias.length - 1, broken: false });
        pos = text.indexOf(alias, pos + 1);
      }
      if (segments.length) yxAitMsg[accountId] = { text: alias, segments };
    });
    return Object.keys(yxAitMsg).length ? { yxAitMsg } : undefined;
  }, []);

  // ── 发送文本消息 ───────────────────────────────────────────────────────────
  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text) return;
    const atExt = buildAtExt(text, mentionedMembers);
    clearInput();
    setReplyMsg(null);
    shouldScrollBottom.current = true;
    store.msgStore
      .sendTextMsgActive({ scene: "team", to: tid, body: text, ...(atExt ? { ext: atExt } : {}) })
      .then(() => { store.msgStore.removeReplyMsgActive(sessionId); })
      .catch(() => { store.msgStore.removeReplyMsgActive(sessionId); Dialog.info("发送失败，请重试"); });
  }, [inputValue, mentionedMembers, buildAtExt, clearInput, setReplyMsg, shouldScrollBottom, store, sessionId, tid]);

  // ── 键盘 Enter/Backspace 逻辑 ────────────────────────────────────────────
  const handleInputKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    if (e.key === 'Backspace') {
      const el = inputRef.current;
      if (!el) return;
      const cursorIndex = el.selectionStart;
      let atIndex;
      let deletedAccountId;
      mentionedMembers.some(({ nick, accountId }) => {
        const alias = `@${nick} `;
        const regex = new RegExp(alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        let match;
        while ((match = regex.exec(inputValue)) !== null) {
          const start = match.index;
          const end   = start + alias.length;
          if (cursorIndex === end) { atIndex = start; deletedAccountId = accountId; return true; }
        }
        return false;
      });
      if (atIndex !== undefined) {
        e.preventDefault();
        const newValue = inputValue.slice(0, atIndex) + inputValue.slice(cursorIndex);
        setInputValue(newValue);
        if (deletedAccountId) {
          setMentionedMembers((prev) => prev.filter((m) => {
            if (m.accountId !== deletedAccountId) return true;
            return newValue.includes(`@${m.nick} `);
          }));
        }
        setTimeout(() => { if (inputRef.current) inputRef.current.setSelectionRange(atIndex, atIndex); }, 0);
      }
    }
  }, [handleSend, inputValue, mentionedMembers]);

  // ── onChange：检测 @ 触发成员选择器 ──────────────────────────────────────
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    const el     = e.target;
    const cursor = el.selectionStart;
    const sub    = newValue.slice(0, cursor);
    const atIdx  = sub.lastIndexOf("@");
    if (atIdx !== -1 && sub.slice(atIdx + 1) === "") {
      atCtx.atStart    = atIdx;
      atCtx.atEnd      = cursor;
      atCtx.inputValue = newValue;
      atCtx.result     = null;
      el.blur();
      setTimeout(() => { general.history.push(`/teamMembers?tid=${tid}&atPicker=1`); }, 50);
    }
  }, [tid]);

  // ── 发送图片/视频 ─────────────────────────────────────────────────────────
  const handleMediaSelect = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = "";
    shouldScrollBottom.current = true;
    const previewImg = URL.createObjectURL(file);
    const isVideo    = file.type.startsWith("video/");
    const cleanup    = () => URL.revokeObjectURL(previewImg);
    const el = listRef.current;
    let ro = null;
    if (el) {
      ro = new ResizeObserver(() => { el.scrollTop = el.scrollHeight; });
      ro.observe(el);
    }
    const finalize = () => {
      if (ro) ro.disconnect();
      requestAnimationFrame(() => { if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); });
    };
    const sendPromise = isVideo
      ? store.msgStore.sendVideoMsgActive({ scene: "team", to: tid, file, previewImg })
      : store.msgStore.sendImageMsgActive({ scene: "team", to: tid, file, previewImg });
    sendPromise
      .then(() => { cleanup(); finalize(); })
      .catch(() => { cleanup(); finalize(); Dialog.info(isVideo ? "视频发送失败" : "图片发送失败"); });
  }, [store, tid, listRef, shouldScrollBottom]);

  return {
    inputRef,
    inputValue,
    setInputValue,
    mentionedMembers,
    insertAtCursor,
    insertMention,
    handleSend,
    handleInputKeyDown,
    handleInputChange,
    handleMediaSelect,
  };
}
